import { CommonModule, formatDate } from '@angular/common';
import { LocalizationService, LocalizationModule } from '@abp/ng.core';
import {
  AfterViewInit,
  Component,
  Inject,
  LOCALE_ID,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { filter, take } from 'rxjs';
import { CompanySiteStore, InformationsAuditedEntityComponent } from '@digiwall/goliath-core';
import { SalesPersonStore } from '@digiwall/goliath-contacts';
import { WorkerService } from '@digiwall/goliath-contacts/proxy';
import { DwActiveLookupRequestDto } from '@digiwall/goliath-core/proxy';
import {
  StorageLocationLookupRequestDto,
  StorageLocationRole,
  StorageLocationService,
  WarehouseLookupRequestDto,
  WarehouseService,
} from '@digiwall/goliath-core/proxy';
import { DwGridDetailNavigationComponent, DwTabsComponent } from '@digiwall/ag-grid';
import {
  BaseDetailPage,
  ChatComponent,
  DeactivatableComponentService,
  DwApiSelectDirective,
  DwDatePickerComponent,
  DwInputRequiredDirective,
  DwKeyboardShortcutDirective,
  ErrorFormControlDirective,
  HistoryComponent,
  NewId,
  ShortcutKey,
} from '@digiwall/core';
import {
  NgbNav,
  NgbNavItem,
  NgbNavItemRole,
  NgbNavLink,
  NgbNavLinkBase,
  NgbNavContent,
  NgbScrollSpy,
  NgbNavOutlet,
} from '@ng-bootstrap/ng-bootstrap';
import { NgxValidateCoreModule } from '@ngx-validate/core';
import {
  NgSelectComponent,
  NgLabelTemplateDirective,
  NgOptionTemplateDirective,
} from '@ng-select/ng-select';
import {
  AutoSizedDropdownDirective,
  chatSidePanelTrigger,
  DwButtonDirective,
  historySidePanelTrigger,
  infoSidePanelTrigger,
  PanelComponent,
  PanelTrigger,
  SidePanelComponent,
} from '@digiwall/ui-kit';
import {
  InventoryCountDocDto,
  InventoryCountDocLineUpdatedDto,
  InventoryCountDocService,
  InventoryCountStatus,
} from '@digiwall/goliath-documents/proxy';
import { GoliathDocumentEntityNames } from '../../../constants/entity-names.constants';
import { GoliathDocumentsEnumerationTypeStore } from '../../../enumeration-types';
import { InventoryCountDocLinesEditorComponent } from './inventory-count-doc-lines/inventory-count-doc-lines-editor.component';

@Component({
  selector: 'document-inventory-count-doc-detail',
  templateUrl: './inventory-count-doc-detail.component.html',
  styleUrl: './inventory-count-doc-detail.component.scss',
  imports: [
    CommonModule,
    PanelComponent,
    DwGridDetailNavigationComponent,
    DwTabsComponent,
    NgbNav,
    NgbNavItem,
    NgbNavItemRole,
    NgbNavLink,
    NgbNavLinkBase,
    NgbNavContent,
    NgbScrollSpy,
    NgbNavOutlet,
    ReactiveFormsModule,
    FormsModule,
    NgxValidateCoreModule,
    DwInputRequiredDirective,
    ErrorFormControlDirective,
    NgSelectComponent,
    NgLabelTemplateDirective,
    NgOptionTemplateDirective,
    AutoSizedDropdownDirective,
    DwDatePickerComponent,
    DwApiSelectDirective,
    DwKeyboardShortcutDirective,
    LocalizationModule,
    SidePanelComponent,
    ChatComponent,
    HistoryComponent,
    InformationsAuditedEntityComponent,
    InventoryCountDocLinesEditorComponent,
    DwButtonDirective,
  ],
})
export class InventoryCountDocDetailComponent
  extends BaseDetailPage<InventoryCountDocDto, InventoryCountDocService>
  implements AfterViewInit
{
  protected triggers = signal<PanelTrigger[]>([]);
  protected noteDirty = signal(false);

  protected readonly chatPanel = viewChild<SidePanelComponent>('chatPanel');
  protected readonly infoPanel = viewChild<SidePanelComponent>('info');
  protected readonly historyPanel = viewChild<SidePanelComponent>('history');

  // The location list is filtered by warehouse (or site); reset these directives when their
  // filter changes so the options reload instead of serving the previous, stale cache.
  private readonly warehouseApiSelect = viewChild<DwApiSelectDirective>('warehouseApiSelect');
  private readonly storageLocationApiSelect = viewChild<DwApiSelectDirective>(
    'storageLocationApiSelect',
  );

  protected GoliathDocumentEntityNames: any = GoliathDocumentEntityNames;
  protected shortcut = ShortcutKey;
  protected currentTab = 'general';

  protected companySiteStore = inject(CompanySiteStore);
  private companySiteLoaded$ = toObservable(this.companySiteStore.loaded);
  protected salesPersonStore = inject(SalesPersonStore);
  protected enumerationTypeStoreDoc = inject(GoliathDocumentsEnumerationTypeStore);

  /** Tracks the entity's persisted status so selectableStatuses stays stable across change detection. */
  private readonly entityStatus = signal<string | null>(null);

  /**
   * Only DRAFT and READY are selectable. An existing document already in a later status keeps
   * its current value as an extra option so the select does not render blank.
   */
  protected readonly selectableStatuses = computed(() => {
    const current = this.entityStatus();
    const allowed: string[] = [InventoryCountStatus.Draft, InventoryCountStatus.Ready];
    return this.enumerationTypeStoreDoc
      .inventoryCountStatus()
      .filter(s => allowed.includes(s.id) || s.id === current);
  });

  constructor(
    route: ActivatedRoute,
    deactivatableComponentService: DeactivatableComponentService,
    service: InventoryCountDocService,
    private formBuilder: FormBuilder,
    private workerService: WorkerService,
    private storageLocationService: StorageLocationService,
    private warehouseService: WarehouseService,
    private localizationService: LocalizationService,
    @Inject(LOCALE_ID) private locale: string,
  ) {
    super(route, deactivatableComponentService, service);
  }

  ngAfterViewInit() {
    this.triggers.set([
      chatSidePanelTrigger(this.chatPanel()),
      infoSidePanelTrigger(this.infoPanel()),
      historySidePanelTrigger(this.historyPanel()),
    ]);
  }

  /** Leaving with an unsaved header or note change should prompt the deactivate guard. */
  public override canDeactivate = () => !this.form.dirty && !this.noteDirty();

  protected override init(params: Params): void {
    this.initForm();

    const id = params['id'];
    if (id !== NewId) {
      this.isCreationMode = false;
      this.getEntity(id);
    } else {
      this.isCreationMode = true;
      this.subscriptionService.addOne(this.service.getDefaultValue(), response => {
        const entity = { ...response, storageLocationIds: [], lines: [] } as InventoryCountDocDto;
        this.entity = entity;
        this.entityStatus.set(entity.status ?? null);
        this.form.reset(entity, { emitEvent: false });
        this.applyStatusEditability();
        this.noteDirty.set(false);
        this.afterNewEntity(entity);
      });
    }
  }

  initForm(): void {
    this.form = this.formBuilder.group({
      companySiteId: [null, Validators.required],
      // Optional warehouse narrowing the location list; persisted on the document.
      warehouseId: [null],
      responsibleId: [null, Validators.required],
      inventoryDate: [null, Validators.required],
      storageLocationIds: [[], Validators.required],
      description: [null],
      status: [null],
      priority: [1, [Validators.required, Validators.min(1), Validators.max(9)]],
      workerId: [null],
    });

    // Changing the site invalidates the warehouse and location selections below it (user-driven change only).
    // Bound via takeUntilDestroyed (not subscriptionService) so it survives getEntity()'s
    // subscriptionService.reset() — otherwise these listeners die immediately in edit mode.
    this.form
      .get('companySiteId')
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.form.get('warehouseId').setValue(null);
        this.form.get('storageLocationIds').setValue([]);
        // The warehouse list is filtered by site, so reload it to drop options for the previous site.
        this.warehouseApiSelect()?.reset();
      });

    // The warehouse narrows the location list, so changing it drops the current location selection.
    this.form
      .get('warehouseId')
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.form.get('storageLocationIds').setValue([]);
        // Reload the location options for the new warehouse (or for the site, when the warehouse is cleared).
        this.storageLocationApiSelect()?.reset();
      });
  }

  protected readonly minInventoryDate = (() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
  })();

  /** Runs after loading an existing document and after an update save (via the entity setter). */
  protected override afterGetEntity(entity: InventoryCountDocDto): void {
    super.afterGetEntity(entity);
    this.entityStatus.set(entity?.status ?? null);
    this.applyStatusEditability();
    this.noteDirty.set(false);
  }

  /**
   * Reset the form without emitting value changes, so reloading/saving does not trigger the
   * companySiteId → storageLocationIds reset that is meant for user edits only.
   */
  protected override assignFormValue(): void {
    this.form.reset(this.entity, { emitEvent: false });
    this.form.markAsPristine();
    // The location/warehouse options were loaded on init, before the entity's site and warehouse were
    // known, and the silent reset above won't refresh them; reload so the lists match the saved values.
    this.warehouseApiSelect()?.reset();
    this.storageLocationApiSelect()?.reset();
  }

  /**
   * Enforces the status-based modification rules on the header form:
   * - DRAFT: everything editable.
   * - READY: only the status is editable (to allow demotion back to DRAFT); other fields locked.
   * - InProgress / Done: fully locked.
   * The note is edited through its own side panel and is always available, independently of this.
   */
  private applyStatusEditability(): void {
    const status = this.entity?.status;
    const headerEditable = status === InventoryCountStatus.Draft;
    const statusEditable =
      status === InventoryCountStatus.Draft || status === InventoryCountStatus.Ready;

    [
      'companySiteId',
      'warehouseId',
      'responsibleId',
      'inventoryDate',
      'storageLocationIds',
      'description',
      'priority',
      'workerId',
    ].forEach(name => this.setControlEnabled(name, headerEditable));
    this.setControlEnabled('status', statusEditable);
  }

  private setControlEnabled(name: string, enabled: boolean): void {
    const control = this.form.get(name);
    if (!control) {
      return;
    }
    if (enabled) {
      control.enable({ emitEvent: false });
    } else {
      control.disable({ emitEvent: false });
    }
  }

  initBreadcrumb(): void {
    if (!this.entity?.id) {
      this.breadcrumbService.set('@entityName', this.localizationService.instant('::Add'));
      return;
    }
    this.subscriptionService.addOne(this.companySiteLoaded$.pipe(filter(Boolean), take(1)), () => {
      const site = this.companySiteStore.entityMap()[this.entity.companySiteId]?.code ?? '';
      const date = this.entity.inventoryDate
        ? formatDate(this.entity.inventoryDate, 'shortDate', this.locale)
        : '';
      this.breadcrumbService.set('@entityName', [site, date].filter(Boolean).join(' - '));
    });
  }

  onTabChanged(tab: string) {
    this.currentTab = tab;
  }

  /**
   * Save strategy driven by the document status:
   * - DRAFT / READY: a single create/update call persists the editable fields together with the
   *   note (the note is part of the update payload). Triggered whenever the form or the note changed.
   * - InProgress / Done: the header is locked, so only the note can change; it is persisted through
   *   its dedicated endpoint.
   */
  protected override save(): void {
    if (this.form.disabled) {
      if (this.canEditNote && this.noteDirty()) {
        this.persistNote();
      }
      return;
    }

    if (this.form.dirty || this.noteDirty()) {
      super.save();
    }
  }

  protected onNoteChanged() {
    this.noteDirty.set(true);
  }

  private get canEditNote(): boolean {
    return !this.isCreationMode && !!this.entity?.id;
  }

  private persistNote(showToast = true): void {
    if (!this.entity?.id) {
      return;
    }
    this.isSaving.set(true);
    this.subscriptionService.addOne(
      this.service.setNote(this.entity.id, this.entity.concurrencyStamp, this.entity.note ?? null),
      concurrencyStamp => {
        this.isSaving.set(false);
        this.entity.concurrencyStamp = concurrencyStamp ?? this.entity.concurrencyStamp;
        this.noteDirty.set(false);
        this.form.markAsPristine();
        if (showToast) {
          this.toastService.success('::SavedSuccessfully');
        }
      },
      () => {
        this.isSaving.set(false);
      },
    );
  }

  protected onLineUpdated(update: InventoryCountDocLineUpdatedDto) {
    if (this.entity) {
      this.entity.concurrencyStamp = update.concurrencyStamp ?? this.entity.concurrencyStamp;
    }
  }

  protected warehouseSelect = (dto: WarehouseLookupRequestDto) => {
    dto.onlyActive = true;
    dto.companySiteId = this.form.get('companySiteId').value;
    dto.queryProperties = ['code', 'name'];
    dto.sorting = [{ property: 'code' }];
    return this.warehouseService.getLookupList(dto);
  };

  protected storageLocationSelect = (dto: StorageLocationLookupRequestDto) => {
    dto.onlyActive = true;
    // Filter by warehouse when set, otherwise by site (a warehouse already belongs to a single site).
    const warehouseId = this.form.get('warehouseId').value;
    if (warehouseId) {
      dto.warehouseId = warehouseId;
    } else {
      dto.companySiteId = this.form.get('companySiteId').value;
    }
    dto.excludedRoles = [StorageLocationRole.Virtual];
    dto.queryProperties = ['code', 'name'];
    dto.sorting = [{ property: 'code' }];
    return this.storageLocationService.getLookupList(dto);
  };

  protected workerSelect = (dto: DwActiveLookupRequestDto<string>) => {
    dto.onlyActive = true;
    dto.sorting = [{ property: 'lastName' }];
    dto.queryProperties = ['code', 'lastName', 'firstName'];
    return this.workerService.getLookupList(dto);
  };
}
